import type { Recipe } from '@recipes/contracts';
import { Button } from '@recipes/ui';
import { CloudDownload, CloudOff } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { toastError } from '@/lib/errors';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { useIsDownloaded } from '@/lib/offline/hooks';
import { offlineManager } from '@/lib/offline/manager';

export interface OfflineToggleProps {
  recipe: Recipe;
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  size?: 'default' | 'sm';
  className?: string;
}

/** "Download for offline" / "Remove offline copy" toggle button (spec 15). */
export function OfflineToggle({
  recipe,
  variant = 'outline',
  size = 'default',
  className,
}: OfflineToggleProps) {
  const { t } = useTranslation();
  const downloaded = useIsDownloaded(recipe.id);
  const online = useOnlineStatus();
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    setBusy(true);
    try {
      if (downloaded) {
        await offlineManager.remove(recipe.id);
        toast.success(t('offline.removed'));
      } else {
        await offlineManager.downloadRecipe(recipe);
        toast.success(t('offline.downloaded'));
      }
    } catch (error) {
      toastError(error, t('offline.downloadFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant={downloaded ? 'secondary' : variant}
      size={size}
      className={className}
      onClick={() => void toggle()}
      loading={busy}
      disabled={!downloaded && !online}
      aria-pressed={downloaded}
      data-testid="offline-toggle"
    >
      {downloaded ? <CloudOff aria-hidden /> : <CloudDownload aria-hidden />}
      {downloaded ? t('offline.remove') : t('offline.download')}
    </Button>
  );
}
